/** @format */

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
    forms,
    IAccount,
    IAccountBasicInfo,
    IAppointmentType,
    IBranding,
    IDownloadableFile,
    IForm,
    IFormTemplateLabel,
    ILocation,
    integrations,
    IPerson,
    IPractitioner,
    IStaffSchedulerDate,
    patients,
    scheduling,
} from "lobbie";
import FormTemplate from "src/classes/FormTemplate";
import { TFormElementValue } from "src/components/shared/forms/utils";
import {
    DEFAULT_ACCOUNT_FROM_EMAIL_ADDRESS,
    DEFAULT_ACCOUNT_FROM_EMAIL_NAME,
    DEFAULT_ACCOUNT_REPLY_TO_EMAIL_ADDRESS,
} from "src/components/staff/config/ConfigBranding";
import { brandingReducer } from "src/redux/reducers/brandingReducer";
import { formBuilderReducer } from "src/redux/reducers/formBuilderReducer";
import { formReducer } from "src/redux/reducers/formReducer";
import { kiosksReducer } from "src/redux/reducers/forms/kiosks/kiosksReducer";
import { formWidthReducer } from "src/redux/reducers/formWidthReducer";
import { iframeReducer } from "src/redux/reducers/iframeReducer";
import { LOBBIE_COLORS } from "src/utils/colors";

import NPSSelfSignupInfo from "src/classes/NPSSelfSignupInfo";
import { formsDashboardReducer } from "src/redux/reducers/forms/formsDashboardReducer";
import { npsSelfSignupInfoReducer } from "src/redux/reducers/npsSelfSignupReducer";
import { IS_LOBBIE_PROD } from "../constants";
import { accountReducer } from "./reducers/accountReducer";
import { appointmentsReducer } from "./reducers/appointmentsReducer";
import { fileDownloadsReducer } from "./reducers/fileDownloadsReducer";
import { formGroupReducer } from "./reducers/formGroupReducer";
import { integrationWizardReducer } from "./reducers/integrationWizardReducer";
import { integratorsReducer } from "./reducers/integratorsReducer";
import { loadingReduxReducer } from "./reducers/loadingReduxReducer";
import { patientSchedulingReducer } from "./reducers/patientSchedulingReducer";
import { staffSchedulerReducer } from "./reducers/staffSchedulerReducer";
import { userReducer } from "./reducers/userReducer";

// new redux api using slices instead of explicit actions and reducers
import appointmentDrawerReducer, {
    AppointmentDrawerState as IAppointmentDrawerState,
    initialStateAppointmentDrawer,
} from "./slices/drawers/appointmentDrawerSlice";
import invoiceDrawerReducer, {
    IInvoiceDrawerState,
    initialStateInvoiceDrawer,
} from "./slices/drawers/invoiceDrawerSlice";
import allergyReducer from "./slices/healthProfileAllergySlice";

import labsViewDrawerReducer, {
    ILabsViewDrawerState,
    initialStateLabsViewDrawer,
} from "./slices/drawers/labsViewDrawerSlice";

import labsOrderDrawerReducer, {
    ILabsOrderDrawerState,
    initialStateLabsOrderDrawer,
} from "./slices/drawers/labsOrderDrawerSlice";

import labsTestConfigDrawerReducer, {
    ILabTestConfigDrawer,
    initialStateLabsTestConfigDrawer,
} from "./slices/drawers/labsTestConfigDrawerSlice";

import addClinicalNoteDrawerSliceReducer, {
    IAddClinicalNoteDrawerState,
    initialStateAddClinicalNoteDrawer,
} from "./slices/drawers/addClinicalNoteDrawerSlice";

import { logger as reduxLogger } from "redux-logger";
import prescriptionSlice from "src/components/pages/staff/prescriptions/hooks/prescription.slice";
import { webappReducer } from "./reducers/webappLayoutReducer";
import containerEntryDrawerReducer, {
    IContainerEntryDrawerState,
    initialStateContainerEntryDrawer,
} from "./slices/drawers/controlled_substance_logging/containerEntryDrawerSlice";
import lotAndContainerDetailsReducer, {
    ILotAndContainerDetailsState,
    initialStateLotAndContainerDetails,
} from "./slices/drawers/controlled_substance_logging/lotAndContainerDetailsSlice";
import medicationConfigDrawerSliceReducer, {
    IMedicationConfigDrawerState,
    initialMedicationConfigDrawerState,
} from "./slices/drawers/controlled_substance_logging/medicationConfigDrawerSlice";

import containerLogDrawerSliceReducer, {
    initialStateContainerLogDrawer,
} from "./slices/drawers/controlled_substance_logging/containerLogDrawerSlice";

import reconcilitationDrawerSliceReducer, {
    initialStateReconciliationDrawer,
} from "./slices/drawers/controlled_substance_logging/reconciliationDrawerSlice";

import { drawerMiddleware, drawerSnapshotMiddleware } from "./slices/drawers/drawerMiddleware";
import drawerStackSliceReducer, {
    initialStateDrawerStack,
} from "./slices/drawers/drawerStackSlice";
import labsVendorConfigDrawerSlice from "./slices/drawers/labsVendorConfigDrawerSlice";
import prescriptionDrawerReducer, {
    initialStatePrescriptionDrawer,
    IPrescriptionDrawerState,
} from "./slices/drawers/prescriptionDrawerSlice";
import taskDashboardDrawerReducer, {
    initialStateTaskDashboard,
    ITaskDashboardState,
} from "./slices/drawers/taskDashboardSlice";
import taskViewEditReducer, {
    initialStateTaskViewEdit,
    ITaskViewEditDrawerState,
} from "./slices/drawers/taskViewEditSlice";
import uploadPatientDocumentsDrawerReducer, {
    initialStateUploadPatientDocumentsDrawer,
    IUploadPatientDocumentsDrawerState,
} from "./slices/drawers/uploadPatientDocumentsDrawerSlice";
import labsGlobalConfigReducer, {
    ILabsGlobalConfigState,
    initialStateLabsGlobalConfigState,
} from "./slices/hooks/labsGlobalConfigSlice";
import { LabsSettingSlice } from "./slices/LabSettings.slice";
import locationSlice from "./slices/Location.slice";
import practitionersSlice from "./slices/Practitioners.slice";

import labcorpConfigurationApi from "./api/LabcorpConfiguration.api";
import labsConfigurationApi from "./api/LabsConfigurationApi";
import staffApi from "./api/StaffApi";
import AppDrawerSlice from "./slices/AppDrawer.slice";
import clinicalNotesDrawerSliceReducer, {
    initialStateClinicalNotesDrawer,
} from "./slices/drawers/clinicalNoteDrawerSlice";
import searchTableRefreshReducer, {
    initialSearchTableRefetchState,
} from "./slices/searchTableRefetchSlice";

import clinicalChartModalSliceReducer, {
    initialStateClinicalChartModal,
} from "./slices/clinicalChartModalSlice";

const reducers = combineReducers({
    npsSelfSignupInfo: npsSelfSignupInfoReducer,
    account: accountReducer,
    appointments: appointmentsReducer,
    branding: brandingReducer,
    iframe: iframeReducer,
    integrators: integratorsReducer,
    loadingRedux: loadingReduxReducer,
    fileDownloads: fileDownloadsReducer,
    form: formReducer,
    formBuilder: formBuilderReducer,
    formGroup: formGroupReducer,
    formWidth: formWidthReducer,
    formsDashboard: formsDashboardReducer,
    kiosks: kiosksReducer,
    patientScheduling: patientSchedulingReducer,
    staffScheduler: staffSchedulerReducer,
    integrationWizard: integrationWizardReducer,
    user: userReducer,
    webappLayout: webappReducer,
    appointmentDrawer: appointmentDrawerReducer,
    invoiceDrawer: invoiceDrawerReducer,
    prescriptionDrawer: prescriptionDrawerReducer,
    labsViewDrawer: labsViewDrawerReducer,
    labsOrderDrawer: labsOrderDrawerReducer,
    labsTestConfigDrawer: labsTestConfigDrawerReducer,
    labsGlobalConfig: labsGlobalConfigReducer,
    addClinicalNoteDrawer: addClinicalNoteDrawerSliceReducer,
    taskDashboardDrawer: taskDashboardDrawerReducer,
    taskViewEditDrawer: taskViewEditReducer,
    uploadPatientDocumentsDrawer: uploadPatientDocumentsDrawerReducer,
    // controlled substance logging
    medicationConfigDrawer: medicationConfigDrawerSliceReducer,
    lotAndContainerDetails: lotAndContainerDetailsReducer,
    containerEntryDrawer: containerEntryDrawerReducer,
    containerLogDrawer: containerLogDrawerSliceReducer,
    drawerStack: drawerStackSliceReducer,
    reconciliationDrawer: reconcilitationDrawerSliceReducer,
    // end of controlled subtance logging
    searchTableRefresh: searchTableRefreshReducer,
    clinicalNotesDrawer: clinicalNotesDrawerSliceReducer,
    clinicalChartModal: clinicalChartModalSliceReducer,
    // app slices
    location: locationSlice.reducer,
    practitioners: practitionersSlice.reducer,
    prescriptionsSlice: prescriptionSlice.reducer,
    labsVendorConfigDrawer: labsVendorConfigDrawerSlice.reducer,
    labSettings: LabsSettingSlice.reducer,
    allergy: allergyReducer,
    [AppDrawerSlice.reducerPath]: AppDrawerSlice.reducer,
    // APIs self-manage state and expect this structure
    [staffApi.reducerPath]: staffApi.reducer,
    [labsConfigurationApi.reducerPath]: labsConfigurationApi.reducer,
    [labcorpConfigurationApi.reducerPath]: labcorpConfigurationApi.reducer,
});

export interface IReduxIntegrationWizardSyncableState {
    appointmentTypes: Record<string, integrations.IAppointmentTypeIntegration>;
    locations: Record<string, integrations.ILocationIntegration>;
    practitioners: Record<string, integrations.IStafferIntegration>;
    accountPatients: Record<string, integrations.IAccountPatientIntegration>;
    appointments: Record<string, integrations.IAppointmentIntegration>;
    formTemplates: Record<string, integrations.IFormTemplateIntegration>;
    formTemplateLabels: Record<string, integrations.IFormTemplateLabelIntegration[]>;
}

export interface IReduxIntegrationWizardState {
    appointmentTypes: IAppointmentType[];
    locations: ILocation[];
    practitioners: IPractitioner[];
    formTemplates: FormTemplate[];
    formTemplateLabels: IFormTemplateLabel[];
    integrator: integrations.IIntegrator;
    syncable: IReduxIntegrationWizardSyncableState;
}

/**
 * @deprecated this Interface is out of date. Use RootState
 */
export interface IAppState {
    accountNPSSelfSignupInfo: {
        accountNPSSelfSignupInfo: NPSSelfSignupInfo | undefined;
    };
    account: {
        account: IAccount | undefined;
        accounts: IAccountBasicInfo[];
        publicUuid: string | undefined;
    };
    appointments: {
        appointmentTypes: IAppointmentType[] | undefined;
        defaultAppointmentType: IAppointmentType | undefined;
    };
    branding: IBranding;
    fileDownloads: {
        show: boolean;
        files: IDownloadableFile[];
    };
    form: {
        form: IForm | undefined;
        formElementAnswers: Record<number, Record<number, TFormElementValue>>;
        storageKey: string | undefined; // key used for local storage of form data including signatures/initials
        anonymousAccountPatient: patients.IAccountPatientLite | undefined;
        formTemplateSectionIndex: number | undefined;
        formTemplateSectionScrolledId: number | undefined;
    };
    formsSend: {
        initialRecipients: patients.IAccountPatient[] | undefined;
    };
    formWidth: number | undefined;
    formsDashboard: {
        formTemplateNames: string[];
    };
    formBuilder: {
        activeFormElementId: number | undefined;
    };
    formGroup: forms.IFormGroup | undefined;
    iframe: {
        url: string | undefined;
    };
    integrators: integrations.IIntegrator[] | undefined;
    integrationWizard: IReduxIntegrationWizardState;
    kiosks: {
        kiosks: [];
        kiosk: undefined;
    };
    loadingRedux: {
        isLoading: boolean;
        fileUploadStatus: number;
        fileDownloadStatus: number;
    };
    location: {
        locationId: number | undefined;
        location: ILocation | undefined;
        locations: ILocation[] | undefined;
        multiLocations: ILocation[] | undefined;
    };
    patientScheduling: scheduling.patient.IPatientScheduling;
    practitioners: IPractitioner[];
    prescriptionsSlice: { sessiontoken?: string };
    staffScheduler: {
        staffSchedulerDate: IStaffSchedulerDate | undefined;
    };
    user: {
        user: IPerson | undefined;
    };
    webappLayout: {
        headerHeight: number | undefined; // height of the header (Lobbie logo, Patient Search, Location Selector, etc.) in pixels
    };
    appointmentDrawer: IAppointmentDrawerState;
    invoiceDrawer: IInvoiceDrawerState;
    prescriptionDrawer: IPrescriptionDrawerState;
    labsViewDrawer: ILabsViewDrawerState;
    labsOrderDrawer: ILabsOrderDrawerState;
    labsTestConfigDrawer: ILabTestConfigDrawer;
    labsGlobalConfig: ILabsGlobalConfigState;
    addClinicalNoteDrawer: IAddClinicalNoteDrawerState;
    taskDashboardDrawer: ITaskDashboardState;
    taskViewEditDrawer: ITaskViewEditDrawerState;
    uploadPatientDocumentsDrawer: IUploadPatientDocumentsDrawerState;
    // controlled substance logging
    medicationConfigDrawer: IMedicationConfigDrawerState;
    lotAndContainerDetails: ILotAndContainerDetailsState;
    containerEntryDrawer: IContainerEntryDrawerState;
}

export const store = (() => {
    const _store = {
        reducer: reducers,
        devTools: !IS_LOBBIE_PROD,
        preloadedState: {
            branding: {
                id: -1,
                isUsingCustomLogoImage: false,
                logoS3Url: "",
                logoCircleColor: LOBBIE_COLORS.primary,
                logoLetterColor: LOBBIE_COLORS.white,
                logoName: "Lobbie",
                fromEmailAddress: DEFAULT_ACCOUNT_FROM_EMAIL_ADDRESS,
                fromEmailName: DEFAULT_ACCOUNT_FROM_EMAIL_NAME,
                replyToEmailAddress: DEFAULT_ACCOUNT_REPLY_TO_EMAIL_ADDRESS,
            },
            form: {
                form: undefined,
                formElementAnswers: {},
                storageKey: undefined,
                formTemplateSectionScrolledId: undefined,
            },
            formWidth: undefined,
            formGroup: undefined,
            formsDashboard: {
                formTemplateNames: [],
            },
            fileDownloads: {
                showFileDownloads: false,
                files: [],
            },
            iframe: {
                url: "",
            },
            integrators: [],
            kiosks: {
                kiosks: [],
                kiosk: undefined,
            },
            location: {
                locationId: undefined,
                location: undefined,
                locations: [],
                multiLocations: [],
            },
            practitioners: [],
            webappLayout: {
                headerHeight: undefined,
            },
            appointmentDrawer: initialStateAppointmentDrawer,
            invoiceDrawer: initialStateInvoiceDrawer,
            prescriptionDrawer: initialStatePrescriptionDrawer,
            labsViewDrawer: initialStateLabsViewDrawer,
            labsOrderDrawer: initialStateLabsOrderDrawer,
            labsTestConfigDrawer: initialStateLabsTestConfigDrawer,
            labsGlobalConfig: initialStateLabsGlobalConfigState,
            addClinicalNoteDrawer: initialStateAddClinicalNoteDrawer,
            taskDashboardDrawer: initialStateTaskDashboard,
            taskViewEditDrawer: initialStateTaskViewEdit,
            uploadPatientDocumentsDrawer: initialStateUploadPatientDocumentsDrawer,
            // controlled substance logging
            medicationConfigDrawer: initialMedicationConfigDrawerState,
            lotAndContainerDetails: initialStateLotAndContainerDetails,
            containerEntryDrawer: initialStateContainerEntryDrawer,
            containerLogDrawer: initialStateContainerLogDrawer,
            reconciliationDrawer: initialStateReconciliationDrawer,
            // end controlled substance logging
            drawerStack: initialStateDrawerStack,
            searchTableRefresh: initialSearchTableRefetchState,
            clinicalNotesDrawer: initialStateClinicalNotesDrawer,
            clinicalChartModal: initialStateClinicalChartModal,
        },
    };

    const defaultMiddlewareOpts = {
        serializableCheck: {
            ignoredActions: ["lobbie/app/drawer/openAppDrawer", "lobbie/app/drawer/closeAppDrawer"], // activeDrawer is not serializable and we don't want/need it to be.
            ignoredPaths: [`${AppDrawerSlice.reducerPath}.activeDrawer`], // activeDrawer is not serializable and we don't want/need it to be.
        },
    };

    const appMiddleware = [
        drawerSnapshotMiddleware,
        drawerMiddleware.middleware,
        staffApi.middleware,
        labsConfigurationApi.middleware,
        labcorpConfigurationApi.middleware,
    ];

    if (IS_LOBBIE_PROD) {
        return configureStore({
            ..._store,
            middleware: (getDefaultMiddleware) =>
                getDefaultMiddleware(defaultMiddlewareOpts).concat(appMiddleware),
        });
    } else {
        // ! TOP-LEVEL AWAIT CRASHES THE FRONTEND
        return configureStore({
            ..._store,
            middleware: (getDefaultMiddleware) =>
                getDefaultMiddleware(defaultMiddlewareOpts).concat([...appMiddleware, reduxLogger]),
        });
    }
})();

/**
 * Reflective type generation that is "always current" for both state and dispatch
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
